import React, { Component } from 'react';
import debounce from 'lodash/debounce';
import classNameBind from 'classnames/bind';
import Spinner from '../../../Spinner';
import { TextField } from '../../../TextField';
import Stack from '../../../Stack';
import Popover from '../../../Popover';
import ActionList from '../../../ActionList';
import Button from '../../../Button';
import styles from './WebsiteList.module.css';
import TypesFilter from '../TypesFilter';
import * as Types from '../../types';
import getTypeIcon from '../../getTypeIcon';

const bindClasses = classNameBind.bind(styles);
const PREFETCH_MARGIN = 5;
const TYPE_FILTER_STORAGE_KEY = 'components.WebsitePicker.type';

const isScrolledToBottom = scrollable => (
  scrollable.offsetHeight + scrollable.scrollTop + PREFETCH_MARGIN >= scrollable.scrollHeight
);

class WebsiteSelect extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoaded: this.isPaginationDisabled(),
      isInitialLoad: !this.isPaginationDisabled(),
      websites: props.websites,
      total: props.pagination.total,
      initialWebsites: props.websites,
      initialTotal: props.pagination.total,
      search: '',
      type: localStorage.getItem(TYPE_FILTER_STORAGE_KEY) || Types.all,
    };

    this.debounceSearch = debounce(this.searchWebsites, 600);
  }

  componentDidMount() {
    this.mounted = true;

    this.setState(
      {
        websites: [],
        isLoaded: false,
        isInitialLoad: true,
      },
      this.searchWebsites,
    );
  }

  static getDerivedStateFromProps(nextProps, state) {
    const { initialWebsites, initialTotal } = state;
    const { websites, pagination: { total } } = nextProps;

    if (initialWebsites !== websites || initialTotal !== total) {
      return {
        websites,
        total,
        initialWebsites: websites,
        initialTotal: total,
      };
    }

    return null;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  isPaginationDisabled = () => {
    const { pagination: { limit, total } } = this.props;

    return total === 0 && limit === 0;
  };

  loadingFinished = () => {
    if (this.mounted) {
      this.setState({ isLoaded: true, isInitialLoad: false });
    }
  };

  shouldLoadMoreItems = () => {
    const { isLoaded, websites, total } = this.state;

    return isScrolledToBottom(this.scrollable) && websites.length < total && isLoaded;
  };

  handleScroll = () => {
    if (this.shouldLoadMoreItems()) {
      const { websites, pagination: { onLoadItems, limit } } = this.props;
      const { search, type } = this.state;

      this.setState(
        { isLoaded: this.isPaginationDisabled() },
        () => {
          onLoadItems(websites.length, limit, search, type, this.loadingFinished);
        },
      );
    }
  };

  handleSearch = (event) => {
    const searchValue = (event.target.value || '').toLowerCase();

    this.setState(
      {
        search: searchValue,
        isInitialLoad: true,
        isLoaded: false,
      },
      this.debounceSearch,
    );
  };

  handleTypeSelect = (type) => {
    localStorage.setItem(TYPE_FILTER_STORAGE_KEY, type);
    this.setState(
      {
        type,
        isInitialLoad: true,
        isLoaded: false,
      },
      this.searchWebsites,
    );
  };

  searchWebsites = () => {
    const { withTypesFilter } = this.props;
    const { search, type } = this.state;

    if (this.isPaginationDisabled()) {
      const { websites } = this.props;

      const filterName = website => website.attributes.name.toLowerCase().includes(search);
      const filterNameAndType = element => (element.type === type && filterName(element));

      const newWebsites = websites.filter(
        (withTypesFilter && type !== Types.all) ? filterNameAndType : filterName,
      );

      this.setState({ websites: newWebsites, isLoaded: true, isInitialLoad: false });
    } else {
      const { pagination: { onLoadItems, limit } } = this.props;

      onLoadItems(0, limit, search, type, this.loadingFinished);
    }
  };

  renderList = () => {
    const { onWebsiteSelected } = this.props;
    const { isLoaded, isInitialLoad, websites } = this.state;
    const noItemsFound = isLoaded && websites.length === 0;

    return (
      <div className={bindClasses('list-wrapper')}>
        {!isLoaded && isInitialLoad && (
          <div className={bindClasses('spinner')}>
            <Spinner />
          </div>
        )}
        <div className={bindClasses({ isInitialLoad })}>
          <ActionList
            groups={noItemsFound ? [] : [
              {
                id: 1,
                items: websites.map(website => ({
                  id: website.id,
                  name: website.attributes.name,
                  icon: {
                    name: getTypeIcon(website.type),
                  },
                  website,
                })),
              },
            ]}
            onItemSelected={(item) => { onWebsiteSelected(item.website); }}
          />
        </div>
        {!isLoaded && !isInitialLoad && (
          <div className={bindClasses('spinner')}>
            <Spinner />
          </div>
        )}
      </div>
    );
  };

  render() {
    const { withTypesFilter } = this.props;
    const { search, type } = this.state;

    const searchInput = (
      <TextField
        name="search"
        icon="search"
        onChange={this.handleSearch}
        value={search}
        clearable
        autoFocus
      />
    );

    return (
      <div className={bindClasses('wrapper')}>
        {withTypesFilter
          ? (
            <Stack spacing="narrow">
              <Stack.Item fill>
                {searchInput}
              </Stack.Item>
              <Stack.Item>
                <Popover
                  activatorElement={(
                    <Button
                      appearance={type === Types.all ? 'subdued' : 'primary'}
                      icon="filter-triangle"
                      className={bindClasses('filter-button')}
                    />
                  )}
                  position="bottom-end"
                  speechBubble
                  childToggle={{
                    onSelected: () => true,
                  }}
                  dataId="WebsiteSelect"
                >
                  <TypesFilter onSelected={this.handleTypeSelect} value={type} />
                </Popover>
              </Stack.Item>
            </Stack>
          )
          : searchInput
        }
        <div
          className={bindClasses('list')}
          ref={(scrollable) => { this.scrollable = scrollable; }}
          onScroll={this.handleScroll}
        >
          {this.renderList()}
        </div>
      </div>
    );
  }
}

WebsiteSelect.defaultProps = {
  withTypesFilter: false,
  pagination: {
    onLoadItems: () => {},
    total: 0,
    limit: 0,
  },
};

WebsiteSelect.displayName = 'WebsiteSelect';

export default WebsiteSelect;
